/**
 * Coach "my week" view (Schedule → Calendar → Week): a Today summary and the
 * next 7 days of booked sessions starting today (rolling, so a Saturday still
 * shows the coming week), one column per day on desktop, stacked on phone. Reads GET /api/coach/schedule?from=&to= (the signed-in coach's own
 * sessions; cancelled ones excluded).
 */

import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, MapPin, Video } from 'lucide-react';
import { api } from '@/lib/api';
import { useIsDesktop } from '@/lib/useIsDesktop';

interface Appt {
  id: string;
  date: string;
  start_time: string;
  end_time: string;
  appointment_type: string | null;
  status: string;
  notes: string | null;
  users: { first_name: string | null; last_name: string | null; email: string; display_name?: string | null } | null;
}

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Local midnight of d. */
function dayOf(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function time12(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m ? `${h12}:${pad(m)} ${ampm}` : `${h12} ${ampm}`;
}

/** Display name, else first + last, else email. Never blank. */
export function clientName(u: Appt['users']): string {
  if (!u) return 'Client';
  return u.display_name?.trim() || `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email;
}

export function CoachWeekView() {
  const isDesktop = useIsDesktop();
  const todayIso = iso(new Date());
  const [start, setStart] = useState(() => dayOf(new Date()));
  const [appts, setAppts] = useState<Appt[]>([]);
  const [todays, setTodays] = useState<Appt[]>([]);
  const [loading, setLoading] = useState(true);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)),
    [start]
  );
  const from = iso(days[0]);
  const to = iso(days[6]);
  const isThisWeek = from === todayIso;

  useEffect(() => {
    setLoading(true);
    api
      .get<{ appointments: Appt[] }>(`/api/coach/schedule?from=${from}&to=${to}`)
      .then((d) => setAppts(d.appointments ?? []))
      .catch(() => setAppts([]))
      .finally(() => setLoading(false));
  }, [from, to]);

  // Today's card always shows today, whichever week is on screen.
  useEffect(() => {
    api
      .get<{ appointments: Appt[] }>(`/api/coach/schedule?from=${todayIso}&to=${todayIso}`)
      .then((d) => setTodays(d.appointments ?? []))
      .catch(() => setTodays([]));
  }, [todayIso]);

  const byDay = useMemo(() => {
    const m = new Map<string, Appt[]>();
    for (const a of appts) m.set(a.date, [...(m.get(a.date) ?? []), a]);
    return m;
  }, [appts]);

  const nowHHMM = `${pad(new Date().getHours())}:${pad(new Date().getMinutes())}`;
  const next = todays.find((a) => a.status === 'upcoming' && a.end_time > nowHHMM);
  const weekCount = appts.length;
  const range = `${days[0].toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${days[6].toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;

  const sessionCard = (a: Appt, compact: boolean) => (
    <div
      key={a.id}
      className="rounded-md px-2.5 py-2"
      style={{ background: 'rgba(201,168,106,0.10)', border: '1px solid rgba(201,168,106,0.30)' }}
    >
      <div className="flex items-center gap-1.5">
        {a.appointment_type === 'virtual'
          ? <Video size={12} style={{ color: 'var(--info)' }} />
          : <MapPin size={12} style={{ color: 'var(--gold)' }} />}
        <span className="text-xs font-semibold" style={{ color: 'var(--cream)' }}>{time12(a.start_time)}</span>
        {!compact && <span className="text-xs" style={{ color: 'var(--gray-500)' }}>– {time12(a.end_time)}</span>}
      </div>
      <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--cream-dim)' }}>{clientName(a.users)}</p>
      {!compact && a.notes && <p className="text-[11px] mt-0.5 truncate" style={{ color: 'var(--gray-500)' }}>{a.notes}</p>}
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Today */}
      <div className="card flex items-center gap-4">
        <div className="text-center px-2">
          <p className="text-3xl" style={{ fontFamily: 'var(--font-display), Georgia, serif', lineHeight: 1.05, color: 'var(--cream)' }}>
            {todays.length}
          </p>
          <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--gray-500)' }}>Today</p>
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium" style={{ color: 'var(--cream)' }}>
            {todays.length === 0 ? 'No sessions today' : `${todays.length} ${todays.length === 1 ? 'session' : 'sessions'} today`}
          </p>
          <p className="text-xs truncate" style={{ color: 'var(--gray-400)' }}>
            {next ? `Next: ${time12(next.start_time)} with ${clientName(next.users)}` : todays.length ? 'All done for today' : 'Enjoy the day'}
          </p>
        </div>
      </div>

      {/* Week header */}
      <div className="flex items-center gap-2">
        <button onClick={() => setStart(new Date(start.getFullYear(), start.getMonth(), start.getDate() - 7))} style={{ color: 'var(--gray-400)' }} aria-label="Previous 7 days">
          <ChevronLeft size={20} />
        </button>
        <p className="text-sm font-semibold" style={{ color: 'var(--cream)' }}>{range}</p>
        <button onClick={() => setStart(new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7))} style={{ color: 'var(--gray-400)' }} aria-label="Next 7 days">
          <ChevronRight size={20} />
        </button>
        {!isThisWeek && (
          <button onClick={() => setStart(dayOf(new Date()))} className="text-xs ml-1" style={{ color: 'var(--gold)' }}>
            Back to today
          </button>
        )}
        <span className="ml-auto text-xs" style={{ color: 'var(--gray-500)' }}>
          {loading ? 'Loading…' : `${weekCount} ${weekCount === 1 ? 'session' : 'sessions'}`}
        </span>
      </div>

      {isDesktop ? (
        <div className="grid grid-cols-7 gap-2">
          {days.map((d) => {
            const key = iso(d);
            const list = byDay.get(key) ?? [];
            const isToday = key === todayIso;
            return (
              <div
                key={key}
                className="rounded-lg p-2 flex flex-col gap-1.5"
                style={{
                  minHeight: 220,
                  background: 'var(--surface)',
                  border: `1px solid ${isToday ? 'var(--gold-muted)' : 'rgba(244,239,227,0.08)'}`,
                }}
              >
                <div className="text-center mb-1">
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: isToday ? 'var(--gold)' : 'var(--gray-500)' }}>
                    {d.toLocaleDateString(undefined, { weekday: 'short' })}
                  </p>
                  <p className="text-lg font-semibold leading-tight" style={{ color: isToday ? 'var(--gold)' : 'var(--cream)' }}>{d.getDate()}</p>
                </div>
                {list.length === 0
                  ? <p className="text-[11px] text-center mt-2" style={{ color: 'var(--gray-600, var(--gray-500))' }}>—</p>
                  : list.map((a) => sessionCard(a, true))}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {days.map((d) => {
            const key = iso(d);
            const list = byDay.get(key) ?? [];
            const isToday = key === todayIso;
            return (
              <div key={key} className="card" style={{ borderColor: isToday ? 'var(--gold-muted)' : undefined }}>
                <p className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: isToday ? 'var(--gold)' : 'var(--gray-400)' }}>
                  {isToday ? 'Today · ' : ''}
                  {d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
                </p>
                {list.length === 0
                  ? <p className="text-xs" style={{ color: 'var(--gray-500)' }}>No sessions</p>
                  : <div className="flex flex-col gap-1.5">{list.map((a) => sessionCard(a, false))}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
